import * as Sentry from "@sentry/gatsby";
import React from "react";

interface ErrorBoundaryProps {
  children: React.ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
}

class ErrorBoundary extends React.Component<
  ErrorBoundaryProps,
  ErrorBoundaryState
> {
  state: ErrorBoundaryState = { hasError: false };

  static getDerivedStateFromError(): ErrorBoundaryState {
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    // Once a boundary handles an error, React 18 stops it before it reaches
    // window.onerror, so Sentry's global handler never sees it. Report it here
    // or adding this boundary would silently cost us the crash reports.
    Sentry.captureException(error, {
      contexts: { react: { componentStack: errorInfo.componentStack } },
    });
  }

  render() {
    if (!this.state.hasError) {
      return this.props.children;
    }

    // Deliberately plain markup: this has to render inside whatever state broke
    // the page, so it avoids app components, context and data fetching.
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-3 p-6 text-center">
        <h1 className="text-2xl font-semibold">Something went wrong</h1>
        <p className="max-w-md text-muted-foreground">
          Sorry, this page failed to load. Reloading usually fixes it.
        </p>
        <p className="max-w-md text-sm text-muted-foreground">
          If it keeps happening, try disabling browser extensions for this site
          or contact support.
        </p>
        <button
          type="button"
          onClick={() => window.location.reload()}
          className="mt-2 rounded-md bg-primary px-4 py-2 text-primary-foreground hover:opacity-90"
        >
          Reload page
        </button>
      </div>
    );
  }
}

export default ErrorBoundary;
